import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Person20Regular,
  ArrowLeft20Regular,
  CheckmarkCircle20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import logoAja2Light from "../../assets/logo.png";
import logoAja2Dark from "../../assets/logodark.png";
import { useAuth } from "../../auth/AuthContext";

export function ForgotPasswordView() {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(
    null,
  );
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark/light mode
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    checkTheme();

    if (typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setResetToken(null);
    setResetEmail(null);
    setIsLoading(true);

    // Request password reset via API
    try {
      const response = await forgotPassword(email);
      if (!response?.ok) {
        throw new Error('Erro ao processar o pedido. Tente novamente.');
      }

      // Show success message (no user enumeration - always show success)
      setIsSuccess(true);
      setResetToken(response?.resetToken ?? null);
      setResetEmail(email);
      setEmail('');
      
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Erro ao processar o pedido. Tente novamente.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-background">
      {/* Auth Card */}
      <div 
        className="w-full max-w-[420px] bg-card shadow-[var(--elevation-sm)] p-8"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src={isDarkMode ? logoAja2Dark : logoAja2Light}
            alt="aja2 - Projectos & Construções" 
            className="h-16 object-contain mb-4"
          />

          {/* Title */}
          <h1 className="text-card-foreground mb-2">
            Recuperar password
          </h1>
          <p className="text-[var(--muted-foreground)] text-center max-w-[320px]">
            Introduza o seu email e enviaremos um link para redefinir a sua password
          </p>
        </div>

        {/* Success Message */}
        {isSuccess ? (
          <div className="space-y-6">
            <div 
              className="flex items-start gap-3 p-4 bg-[var(--accent)] border border-[var(--primary)]"
              style={{ borderRadius: 'var(--radius)' }}
            >
              <CheckmarkCircle20Regular className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-card-foreground mb-1">
                  Email enviado
                </p>
                <p className="text-[var(--muted-foreground)]">
                  Se existir uma conta associada a este email, receberá instruções para redefinir a sua password.
                </p>
              </div>
            </div>

            {/* Back to Sign In */}
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full h-10 bg-[var(--secondary)] border border-[var(--border)] text-[var(--secondary-foreground)] transition-all hover:bg-[var(--accent)] active:opacity-80 flex items-center justify-center gap-2"
              style={{ borderRadius: 'var(--radius-button)' }}
            >
              <ArrowLeft20Regular />
              Voltar ao início de sessão
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label 
                htmlFor="email" 
                className="block text-card-foreground"
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]">
                  <Person20Regular />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="seu.email@exemplo.pt"
                  className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                  style={{ borderRadius: 'var(--radius)' }}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div 
                className="flex items-start gap-2 p-3 bg-[var(--destructive)] bg-opacity-10 border border-[var(--destructive)] border-opacity-30"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <Warning20Regular className="text-[var(--destructive)] flex-shrink-0 mt-0.5" />
                <p className="text-[var(--destructive)] text-sm">
                  {error}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-10 bg-[var(--primary)] text-[var(--primary-foreground)] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:opacity-80"
              style={{ borderRadius: 'var(--radius-button)' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg 
                    className="animate-spin h-4 w-4" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  A enviar...
                </span>
              ) : (
                'Enviar link de recuperação'
              )}
            </button>

            {/* Back to Sign In Link */}
            <div className="text-center">
              <button
                type="button"
                disabled={isLoading}
                className="text-[var(--primary)] hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2"
                onClick={() => navigate('/auth/login')}
              >
                <ArrowLeft20Regular />
                Voltar ao início de sessão
              </button>
            </div>
          </form>
        )}

        {/* Temporary Link to Test Reset Password Page */}
        {resetToken && resetEmail && (
          <div 
            className="mt-6 p-3 bg-[var(--accent)] border border-[var(--border)]"
            style={{ borderRadius: 'var(--radius)' }}
          >
            <p className="text-[var(--accent-foreground)] text-xs mb-2">
              <strong>Link temporário (remover em produção):</strong>
            </p>
            <button
              onClick={() =>
                navigate(
                  `/auth/reset-password?email=${encodeURIComponent(
                    resetEmail,
                  )}&token=${encodeURIComponent(resetToken)}`,
                )
              }
              className="text-[var(--primary)] hover:underline text-xs"
            >
              Testar página de redefinir password →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
